"use client";
import { jsx as _jsx } from "react/jsx-runtime";
import * as React from "react";
import { WebbridgeProvider } from "@tapcart/app-studio-sdk";
import { createStorefrontBridgeClient } from "./createStorefrontBridgeClient";
import { fetchIdentityFromHost, installIframeActions, isInIframe, } from "./iframe/postMessageActions";
/**
 * Drop-in replacement for the React-Native-targeted StorefrontBridgeProvider
 * in apps/tapcart-ssr-app/lib/context-providers/webbridge.tsx, for use when
 * the SSR app is serving a "web mode" layout (rendered on a Shopify
 * storefront via Theme App Extension, not inside a React Native WebView).
 *
 * Constructs a web-backed webbridgeClient once on mount and feeds it to
 * the SDK's existing WebbridgeProvider, so every consumer of useTapcart()
 * / useVariables() / useActions() works unchanged. Internal mutable state
 * (variables store, event subscriptions) lives on the client instance.
 *
 * Iframe detection happens after mount: if the bridge is running inside a
 * cross-origin iframe (Tapcart wishlist theme app block scenario), the
 * three same-origin-bound actions (cart/add, product/open,
 * authentication/open) are swapped for postMessage-to-parent versions that
 * the storefront's host-bridge.js asset services. Top-level browser usage
 * keeps the direct implementations.
 */
export const StorefrontBridgeProvider = ({ config, children, }) => {
    var _a;
    const [client] = React.useState(() => createStorefrontBridgeClient(config));
    // Single mount-time bootstrap that handles both deployment modes:
    //
    //   * IFRAME mode (production, Theme App Extension serving Tapcart
    //     content on a merchant storefront): install postMessage overrides
    //     for the same-origin actions (cart/auth/product nav), then ask the
    //     parent host-bridge.js for the Shopify-verified `customerId` via
    //     App Proxy and stamp it onto `variables.customer`. If a customer
    //     comes back, fire an initial `wishlists/get` so the block sees
    //     server-persisted state instead of the localStorage default the
    //     VariablesStore constructor seeded.
    //
    //   * TOP-LEVEL mode (dev / direct-embed): no iframe overrides, no
    //     identity bootstrap (customer comes from SSR config). If a customer
    //     is already configured, still fire the initial `wishlists/get`.
    //
    // Single useEffect avoids ordering bugs between "install overrides",
    // "set customer", and "fire wishlists/get" — they're naturally
    // sequenced here.
    React.useEffect(() => {
        var _a;
        const cleanupCallbacks = [];
        const fireInitialWishlistsGet = () => {
            var _a, _b;
            (_b = (_a = client.actions).action) === null || _b === void 0 ? void 0 : _b.call(_a, "wishlists/get", null, {
                onError: (err) => {
                    // Best-effort bootstrap — block can still render with the
                    // localStorage default and recover on first user interaction.
                    // eslint-disable-next-line no-console
                    console.warn("[storefront-bridge] initial wishlists/get failed", err);
                },
            });
        };
        if (isInIframe()) {
            cleanupCallbacks.push(installIframeActions());
            fetchIdentityFromHost()
                .then(({ customerId }) => {
                if (customerId) {
                    client.setCustomer({ id: customerId });
                    fireInitialWishlistsGet();
                }
            })
                .catch((err) => {
                // eslint-disable-next-line no-console
                console.warn("[storefront-bridge] identity bootstrap failed", err);
            });
        }
        else if ((_a = config.customer) === null || _a === void 0 ? void 0 : _a.id) {
            fireInitialWishlistsGet();
        }
        return () => {
            cleanupCallbacks.forEach((cb) => cb());
        };
    }, [client, (_a = config.customer) === null || _a === void 0 ? void 0 : _a.id]);
    return (_jsx(WebbridgeProvider, { webbridgeClient: client, children: children }));
};
